import React, { useCallback, useMemo, useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Tabs,
  Tab,
  Input,
  Select,
  SelectItem,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { type Control, Controller } from 'react-hook-form';
import { CtyButton } from '@circuitry-ai/doc-ui';
import type { EditAgentFormValues } from './voiceAgentEditFormTypes';
import {
  type VoiceAgentVariableDirection,
  type VoiceAgentVariablesFormShape,
  emptyVoiceAgentVariables,
  getVariablesForDirection,
} from './voiceAgentVariablesUtils';

const DIRECTION_TAB_CLASS_NAMES = {
  tabList: 'gap-4 border-b border-divider',
  cursor: 'bg-primary',
  tab: 'h-9 px-2',
  tabContent: 'group-data-[selected=true]:text-primary text-sm',
};

type VariableModalState =
  | { kind: 'closed' }
  | {
      kind: 'add' | 'edit';
      direction: VoiceAgentVariableDirection;
      variableKey: string;
      variableValue: string;
      originalKey?: string;
    };

type VoiceAgentVariablesSectionProps = Readonly<{
  control: Control<EditAgentFormValues>;
}>;

function VariablesTable({
  entries,
  onEdit,
  onDelete,
}: Readonly<{
  entries: Array<{ key: string; value: string }>;
  onEdit: (key: string) => void;
  onDelete: (key: string) => void;
}>) {
  if (entries.length === 0) {
    return (
      <p className="text-sm text-default-400 py-4 text-center border border-dashed border-divider rounded-medium">
        No variables configured for this direction.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-medium border border-divider">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-divider bg-default-100">
            <th className="text-left font-semibold text-default-600 px-4 py-2.5 w-[40%]">Variable</th>
            <th className="text-left font-semibold text-default-600 px-4 py-2.5">Value</th>
            <th className="text-right font-semibold text-default-600 px-4 py-2.5 w-24">Actions</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(({ key, value }) => (
            <tr key={key} className="border-b border-divider last:border-b-0">
              <td className="px-4 py-2.5 font-medium text-foreground align-top">{key}</td>
              <td className="px-4 py-2.5 text-default-700 align-top break-all">{value}</td>
              <td className="px-4 py-2.5 align-top">
                <div className="flex justify-end gap-1">
                  <CtyButton
                    isIconOnly
                    size="sm"
                    variant="light"
                    aria-label={`Edit ${key}`}
                    onPress={() => onEdit(key)}
                  >
                    <Icon icon="mdi:pencil-outline" width={18} />
                  </CtyButton>
                  <CtyButton
                    isIconOnly
                    size="sm"
                    variant="light"
                    color="danger"
                    aria-label={`Delete ${key}`}
                    onPress={() => onDelete(key)}
                  >
                    <Icon icon="mdi:delete-outline" width={18} />
                  </CtyButton>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VoiceAgentVariablesEditor({
  value,
  onChange,
}: Readonly<{
  value: VoiceAgentVariablesFormShape | undefined;
  onChange: (next: VoiceAgentVariablesFormShape) => void;
}>) {
  const [activeDirection, setActiveDirection] = useState<VoiceAgentVariableDirection>('inbound');
  const [modalState, setModalState] = useState<VariableModalState>({ kind: 'closed' });
  const [modalError, setModalError] = useState<string | null>(null);

  const variables = value ?? emptyVoiceAgentVariables();

  const inboundEntries = useMemo(
    () =>
      Object.entries(getVariablesForDirection(variables, 'inbound'))
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, entryValue]) => ({ key, value: entryValue })),
    [variables]
  );

  const outboundEntries = useMemo(
    () =>
      Object.entries(getVariablesForDirection(variables, 'outbound'))
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, entryValue]) => ({ key, value: entryValue })),
    [variables]
  );

  const openAddModal = useCallback(() => {
    setModalError(null);
    setModalState({
      kind: 'add',
      direction: activeDirection,
      variableKey: '',
      variableValue: '',
    });
  }, [activeDirection]);

  const openEditModal = useCallback(
    (direction: VoiceAgentVariableDirection, key: string) => {
      const currentValue = variables[direction]?.[key] ?? '';
      setModalError(null);
      setModalState({
        kind: 'edit',
        direction,
        variableKey: key,
        variableValue: currentValue,
        originalKey: key,
      });
    },
    [variables]
  );

  const closeModal = useCallback(() => {
    setModalState({ kind: 'closed' });
    setModalError(null);
  }, []);

  const deleteVariable = useCallback(
    (direction: VoiceAgentVariableDirection, key: string) => {
      const directionMap = variables[direction] ? { ...variables[direction]! } : {};
      delete directionMap[key];
      onChange({
        ...variables,
        [direction]: directionMap,
      });
    },
    [onChange, variables]
  );

  const saveModal = useCallback(() => {
    if (modalState.kind === 'closed') return;

    const trimmedKey = modalState.variableKey.trim();
    const trimmedValue = modalState.variableValue.trim();
    if (!trimmedKey) {
      setModalError('Variable name is required.');
      return;
    }
    if (!trimmedValue) {
      setModalError('Value is required.');
      return;
    }

    const directionMap = variables[modalState.direction]
      ? { ...variables[modalState.direction]! }
      : {};
    const duplicateKey =
      trimmedKey in directionMap &&
      !(modalState.kind === 'edit' && modalState.originalKey === trimmedKey);
    if (duplicateKey) {
      setModalError('A variable with this name already exists for this direction.');
      return;
    }

    if (modalState.kind === 'edit' && modalState.originalKey && modalState.originalKey !== trimmedKey) {
      delete directionMap[modalState.originalKey];
    }
    directionMap[trimmedKey] = trimmedValue;

    onChange({
      ...variables,
      [modalState.direction]: directionMap,
    });
    closeModal();
  }, [closeModal, modalState, variables]);

  const isModalOpen = modalState.kind !== 'closed';
  const modalForm =
    modalState.kind === 'closed'
      ? null
      : modalState;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-foreground">System Variables</h3>
          <p className="text-sm text-default-500 mt-0.5">
            Key-value variables available to the assistant for each call direction.
          </p>
        </div>
        <CtyButton
          size="sm"
          color="primary"
          variant="flat"
          startContent={<Icon icon="mdi:plus" width={16} />}
          onPress={openAddModal}
        >
          Add Variable
        </CtyButton>
      </div>

      <Tabs
        aria-label="Variable direction"
        variant="underlined"
        selectedKey={activeDirection}
        onSelectionChange={(key) => setActiveDirection(key as VoiceAgentVariableDirection)}
        classNames={DIRECTION_TAB_CLASS_NAMES}
      >
        <Tab key="inbound" title="Inbound">
          <div className="pt-4">
            <VariablesTable
              entries={inboundEntries}
              onEdit={(key) => openEditModal('inbound', key)}
              onDelete={(key) => deleteVariable('inbound', key)}
            />
          </div>
        </Tab>
        <Tab key="outbound" title="Outbound">
          <div className="pt-4">
            <VariablesTable
              entries={outboundEntries}
              onEdit={(key) => openEditModal('outbound', key)}
              onDelete={(key) => deleteVariable('outbound', key)}
            />
          </div>
        </Tab>
      </Tabs>

      <Modal isOpen={isModalOpen} onOpenChange={(open) => !open && closeModal()} placement="center">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            {modalForm?.kind === 'edit' ? 'Edit Variable' : 'Add Variable'}
          </ModalHeader>
          <ModalBody className="gap-4">
            {modalForm ? (
              <>
                <Select
                  label="Mode"
                  selectedKeys={[modalForm.direction]}
                  onSelectionChange={(keys) => {
                    const next = Array.from(keys)[0] as VoiceAgentVariableDirection | undefined;
                    if (!next) return;
                    setModalState((prev) =>
                      prev.kind === 'closed' ? prev : { ...prev, direction: next }
                    );
                    setModalError(null);
                  }}
                  variant="bordered"
                  isDisabled={modalForm.kind === 'edit'}
                >
                  <SelectItem key="inbound">Inbound</SelectItem>
                  <SelectItem key="outbound">Outbound</SelectItem>
                </Select>
                <Input
                  label="Variable"
                  placeholder="company_name"
                  value={modalForm.variableKey}
                  onValueChange={(variableKey) => {
                    setModalState((prev) =>
                      prev.kind === 'closed' ? prev : { ...prev, variableKey }
                    );
                    setModalError(null);
                  }}
                  variant="bordered"
                />
                <Input
                  label="Value"
                  placeholder="Circuitry AI"
                  value={modalForm.variableValue}
                  onValueChange={(variableValue) => {
                    setModalState((prev) =>
                      prev.kind === 'closed' ? prev : { ...prev, variableValue }
                    );
                    setModalError(null);
                  }}
                  variant="bordered"
                />
                {modalError ? <p className="text-tiny text-danger">{modalError}</p> : null}
              </>
            ) : null}
          </ModalBody>
          <ModalFooter>
            <CtyButton variant="flat" onPress={closeModal}>
              Cancel
            </CtyButton>
            <CtyButton color="primary" onPress={saveModal}>
              {modalForm?.kind === 'edit' ? 'Save' : 'Add'}
            </CtyButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}

export function VoiceAgentVariablesSection({ control }: VoiceAgentVariablesSectionProps) {
  return (
    <Controller
      name="variables"
      control={control}
      render={({ field }) => (
        <VoiceAgentVariablesEditor
          value={field.value}
          onChange={(next) => {
            field.onChange(next);
            field.onBlur();
          }}
        />
      )}
    />
  );
}
